import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Layout from '@/components/feature/Layout';
import EventGridCard from '@/components/feature/EventCard';
import { StatusBadge, CategoryTag, heatLevel } from '@/components/feature/EventBits';
import { events } from '@/mocks/events';
import { setPageMeta, injectJsonLd, clearJsonLd, buildBreadcrumbLd, SITE_BASE } from '@/utils/seo';

export default function EventDetail() {
  const { id } = useParams();
  const [copied, setCopied] = useState(false);

  const event = useMemo(() => events.find((e) => e.id === id), [id]);

  useEffect(() => {
    if (!event) return;
    setPageMeta({
      title: `${event.title}时间轴梳理 - 瓜迹吃瓜事件经过与进展`,
      description: `${event.summary} ${event.followText || ''} 瓜迹按节点梳理${event.category}相关热点事件，从爆发到后续追踪持续更新。`,
      keywords: `${event.title},吃瓜事件时间轴,吃瓜事件经过,${event.category},吃瓜事件进展`,
      path: `/event/${event.id}`,
    });
    const c1 = injectJsonLd(
      buildBreadcrumbLd([
        { name: '首页', path: '/' },
        { name: '事件时间轴', path: '/events' },
        { name: event.title, path: `/event/${event.id}` },
      ]),
    );
    const c2 = injectJsonLd({
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: event.title,
      description: event.summary,
      image: event.cover,
      datePublished: event.started,
      dateModified: event.updated,
      articleSection: event.category,
      keywords: event.tags.join(','),
      author: { '@type': 'Organization', name: '瓜迹' },
      publisher: { '@type': 'Organization', name: '瓜迹', url: SITE_BASE },
      mainEntityOfPage: `${SITE_BASE}/event/${event.id}`,
    });
    return () => {
      clearJsonLd();
      c1();
      c2();
    };
  }, [event]);

  const related = useMemo(() => {
    if (!event) return [];
    return [...events]
      .filter((e) => e.id !== event.id)
      .sort(
        (a, b) =>
          (b.category === event.category ? 1 : 0) - (a.category === event.category ? 1 : 0) || b.heat - a.heat,
      )
      .slice(0, 3);
  }, [event]);

  if (!event) {
    return (
      <Layout>
        <div className="mx-auto flex max-w-3xl flex-col items-center px-4 py-24 text-center">
          <i className="ri-error-warning-line text-5xl text-primary-400" />
          <h1 className="mt-4 font-heading text-2xl font-semibold text-foreground-950">没有找到该起事件</h1>
          <p className="mt-3 text-sm text-foreground-600">链接可能已过期或事件编号有误，回到时间轴列表看看最新收录吧。</p>
          <Link to="/events" className="mt-6 inline-flex items-center gap-2 rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 hover:bg-primary-600">
            <i className="ri-arrow-left-line" /> 返回事件时间轴
          </Link>
        </div>
      </Layout>
    );
  }

  const last = event.nodes[event.nodes.length - 1];
  const shareUrl = typeof window !== 'undefined' ? window.location.href : `${SITE_BASE}/event/${event.id}`;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch {
      setCopied(false);
    }
  };

  return (
    <Layout>
      <section className="mx-auto max-w-7xl px-4 py-8 md:px-6 md:py-12">
        <nav aria-label="面包屑" className="flex flex-wrap items-center gap-1.5 text-xs text-foreground-500">
          <Link to="/" className="hover:text-primary-600">首页</Link>
          <i className="ri-arrow-right-s-line" />
          <Link to="/events" className="hover:text-primary-600">事件时间轴</Link>
          <i className="ri-arrow-right-s-line" />
          <Link to={`/events?cat=${encodeURIComponent(event.category)}`} className="hover:text-primary-600">{event.category}</Link>
          <i className="ri-arrow-right-s-line" />
          <span className="max-w-[18rem] truncate text-foreground-800">{event.title}</span>
        </nav>

        {/* 事件头 */}
        <header className="mt-5 overflow-hidden rounded-xl border border-background-200 bg-background-50">
          <div className="relative h-56 w-full overflow-hidden md:h-72">
            <img src={event.cover} alt={event.coverAlt} className="h-full w-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/75 via-foreground-950/20 to-transparent" />
            <div className="absolute inset-x-0 bottom-0 p-5 md:p-7">
              <div className="flex flex-wrap items-center gap-2">
                <span className="rounded-md bg-background-50/15 px-2.5 py-1 text-xs font-medium text-background-50 backdrop-blur-sm">事件编号 #{event.id.toUpperCase().slice(0, 6)}</span>
                <StatusBadge status={event.status} />
              </div>
              <h1 className="mt-3 font-heading text-2xl font-semibold leading-snug text-background-50 md:text-3xl">{event.title}</h1>
            </div>
          </div>
          <div className="flex flex-col gap-4 border-t border-background-200 p-5 md:flex-row md:items-center md:justify-between md:p-6">
            <p className="flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-foreground-600">
              <span className="flex items-center gap-1.5"><i className="ri-play-circle-line text-accent-500" />爆发 <b className="tabular-nums">{event.started.slice(5).replace('-', '/')}</b></span>
              <span className="flex items-center gap-1.5"><i className="ri-focus-3-line text-primary-500" />更新 <b className="tabular-nums">{event.updated.slice(5).replace('-', '/')}</b></span>
              <span className="flex items-center gap-1.5"><i className="ri-node-tree" />{event.nodes.length} 个节点</span>
              <span className="flex items-center gap-1.5"><i className="ri-fire-line" />热度 <b className="tabular-nums">{event.heat}</b> · {heatLevel(event.heat)}</span>
            </p>
            <button type="button" onClick={copyLink} className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-100 px-3.5 py-2 text-xs font-medium text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700">
              <i className={`${copied ? 'ri-check-line text-primary-600' : 'ri-link'} `} /> {copied ? '已复制链接' : '复制追更链接'}
            </button>
          </div>
        </header>

        {/* 主体 */}
        <div className="mt-8 grid gap-8 lg:grid-cols-3">
          {/* 左时间轴 */}
          <div className="lg:col-span-2">
            <div className="flex items-center justify-between">
              <h2 className="flex items-center gap-2 font-heading text-xl font-semibold text-foreground-950 md:text-2xl">
                <i className="ri-time-line text-primary-500" /> 事件时间轴节点
              </h2>
              <span className="text-xs text-foreground-400">按时间正序 · 从上往下看发展</span>
            </div>

            <ol className="relative mt-6 space-y-6 before:absolute before:bottom-3 before:left-[11px] before:top-3 before:w-0.5 before:bg-gradient-to-b before:from-primary-400 before:via-accent-300 before:to-background-200">
              {event.nodes.map((n, i) => {
                const isFirst = i === 0;
                const isLast = i === event.nodes.length - 1;
                return (
                  <li key={n.date + n.title} className="relative pl-12">
                    <span
                      className={`absolute left-0 top-1 flex h-[22px] w-[22px] items-center justify-center rounded-full border-2 ${
                        isLast
                          ? 'border-accent-500 bg-accent-500'
                          : isFirst
                            ? 'border-primary-500 bg-primary-500'
                            : 'border-background-300 bg-background-50'
                      }`}
                    >
                      <span className={`h-1.5 w-1.5 rounded-full ${isLast ? 'bg-background-50 pulse-dot' : isFirst ? 'bg-background-50' : 'bg-background-300'}`} />
                    </span>

                    <article className={`rounded-xl border p-4 md:p-5 ${isLast ? 'border-accent-300 bg-accent-50/40' : 'border-background-200 bg-background-50'} transition-colors`}>
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-sm font-semibold text-foreground-950 tabular-nums">
                          {n.date.slice(0, 4)}<span className="mx-1 text-foreground-400">/</span>{n.date.slice(5).replace('-', '/')}
                        </span>
                        {n.tag && (
                          <span className={`rounded-md px-2 py-0.5 text-xs font-medium ${isLast ? 'bg-accent-200 text-accent-900' : 'bg-secondary-100 text-secondary-800'}`}>
                            {n.tag}
                          </span>
                        )}
                        <span className="ml-auto text-[11px] text-foreground-400 tabular-nums">节点 {i + 1}</span>
                      </div>
                      <h3 className="mt-2 text-base font-semibold text-foreground-950 md:text-lg">{n.title}</h3>
                      <p className="mt-1.5 text-sm leading-relaxed text-foreground-600">{n.text}</p>
                    </article>
                  </li>
                );
              })}
            </ol>

            {event.followText && (
              <div className="mt-6 flex items-start gap-3 rounded-xl border border-dashed border-primary-300 bg-primary-50/60 p-4">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-primary-500 text-background-50"><i className="ri-live-line text-lg" /></span>
                <div>
                  <p className="text-sm font-semibold text-primary-900">当前进展</p>
                  <p className="mt-1 text-sm leading-relaxed text-primary-800">{event.followText}</p>
                </div>
              </div>
            )}

            {/* 相关推荐 */}
            {related.length > 0 && (
              <div className="mt-10">
                <h2 className="flex items-center gap-2 font-heading text-xl font-semibold text-foreground-950 md:text-2xl">
                  <i className="ri-file-list-3-line text-primary-500" /> 相关热点追踪
                </h2>
                <div className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
                  {related.map((r) => (
                    <EventGridCard key={r.id} event={r} />
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* 右信息栏 */}
          <aside className="space-y-5 lg:sticky lg:top-28 lg:self-start">
            <div className="rounded-xl border border-accent-300 bg-accent-50/60 p-5">
              <p className="flex items-center gap-2 text-sm font-semibold text-accent-900"><i className="ri-focus-3-line" /> 最新节点</p>
              <p className="mt-3 font-heading text-lg font-medium text-foreground-950">{last.title}</p>
              <p className="mt-1 text-xs text-foreground-500 tabular-nums">{last.date} · {event.updated.slice(0, 4)}</p>
              <div className="mt-3 flex flex-wrap gap-2">
                <StatusBadge status={event.status} />
                <CategoryTag category={event.category} />
              </div>
            </div>

            <div className="rounded-xl border border-background-200 bg-background-50 p-5">
              <h3 className="flex items-center gap-2 text-sm font-semibold text-foreground-950"><i className="ri-file-info-line text-primary-500" /> 事件档案</h3>
              <dl className="mt-4 space-y-3 text-sm">
                {[
                  { k: '所属栏目', v: event.category },
                  { k: '爆发时间', v: event.started },
                  { k: '最近更新', v: event.updated },
                  { k: '节点数量', v: `${event.nodes.length} 个` },
                  { k: '热度状态', v: heatLevel(event.heat) },
                ].map((r) => (
                  <div key={r.k} className="flex items-center justify-between border-b border-dashed border-background-200 pb-2 last:border-0 last:pb-0">
                    <dt className="text-foreground-500">{r.k}</dt>
                    <dd className="font-medium text-foreground-900 tabular-nums">{r.v}</dd>
                  </div>
                ))}
              </dl>
              <div className="mt-4">
                <p className="text-xs text-foreground-400">热度指数</p>
                <div className="mt-1.5 h-2.5 overflow-hidden rounded-full bg-secondary-100">
                  <div className="h-full rounded-full bg-gradient-to-r from-accent-400 via-primary-400 to-primary-500" style={{ width: `${event.heat}%` }} />
                </div>
                <p className="mt-1.5 text-right text-xs text-foreground-500 tabular-nums">{event.heat} / 100</p>
              </div>
            </div>

            <div className="rounded-xl border border-background-200 bg-background-50 p-5">
              <h3 className="flex items-center gap-2 text-sm font-semibold text-foreground-950"><i className="ri-price-tag-3-line text-accent-500" /> 相关标签</h3>
              <div className="mt-3 flex flex-wrap gap-2">
                {event.tags.map((t) => (
                  <Link key={t} to={`/events?q=${encodeURIComponent(t)}`} className="rounded-full border border-background-200 bg-background-100 px-3 py-1 text-xs text-foreground-600 transition-colors hover:border-accent-300 hover:bg-accent-50 hover:text-accent-900">
                    #{t}
                  </Link>
                ))}
              </div>
            </div>

            <div className="rounded-xl bg-secondary-100/80 p-5">
              <p className="text-sm font-medium text-secondary-900">这条时间轴还在更新</p>
              <p className="mt-1.5 text-xs leading-relaxed text-secondary-700">事件出现新进展时会持续补全节点。欢迎把公开可查的最新动态反馈给我们。</p>
              <a href="mailto:hello@guaji.team" className="mt-3 inline-flex items-center gap-1.5 whitespace-nowrap rounded-md bg-secondary-500 px-3 py-2 text-xs font-medium text-background-50 transition-colors hover:bg-secondary-600">
                <i className="ri-mail-send-line" /> 反馈新进展
              </a>
            </div>
          </aside>
        </div>
      </section>
    </Layout>
  );
}